import type { ComponentType, FC, PropsWithChildren } from "react";
import { useEffect, useState } from "react";

import { useIsLoggedIn } from "@jti/app-context/PageAuthContext";
import { Loader } from "@jti/core-components/components/Loader";
import { toast } from "@jti/core-components/components/Toaster";
import { isApiErrorWithErrorCode, translateBackendErrorCode } from "@jti/fe-common";
import { getUser } from "@jti/rest-api";
import { type ArticleDataType } from "@jti/rest-api-types";
import type { BlogItemImage, BlogItemVideo } from "@jti/ui/components/BlogListing/BlogItem";
import { useLoyaltyApi } from "@jti/ui/hooks/useLoyaltyApi";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { getImageSource } from "@jti/ui/utils/getImageSource";

import useLoggedUser from "../../hooks/useLoggedUser";
import {
  QuizBannerBottom,
  type QuizBannerContent,
  QuizBannerTop,
  QuizContent,
  type QuizContentConfig,
} from "../LoyaltyQuiz";
import { PointsTrigger } from "../PointsTrigger";

import { ArticleRestrictedContentHeader } from "./ArticleRestrictedContentHeader/ArticleRestrictedContentHeader";
import { PreviewContent } from "./PreviewContent/PreviewContent";

import { ArticleRestrictedContentResponsiveContainer } from "./ArticleRestrictedResponsiveContainer";
import { useQuizBanner } from "./useQuizBanner";

import styles from "./ArticleRestrictedContent.module.scss";

type ArticleMetadata = {
  title: string;
  subtitle: string;
  creationDate: string;
  articleType?: "article" | "video" | "quiz";
  controlColor?: "dark" | "light";
  articleId?: string;
  image?: BlogItemImage | null;
  video?: BlogItemVideo | null;
  headerMediaType?: "image" | "video";
};

type RichTextComponentProps = { baseCssClass?: string; text: string };
export type RichTextComponent = ComponentType<RichTextComponentProps>;

export type ArticleRestrictedContentProps = {
  primaryBtnUrl: string;
  secondaryBtnUrl: string;
  primaryBtnUrlRestricted: string;
  previewContent?: string;
  restrictImage: BlogItemImage | null;
  disableTiers?: boolean;
  article: ArticleMetadata;
  isInEditor?: boolean;
  richTextComponent: RichTextComponent;
  percentageToWatch?: number;
  quizBannerContent?: QuizBannerContent;
  quizContentConfig?: QuizContentConfig;
};

export type ArticleDataPartial = Omit<ArticleDataType, "article_id">;

export const ArticleRestrictedContent: FC<PropsWithChildren<ArticleRestrictedContentProps>> = ({
  primaryBtnUrl,
  secondaryBtnUrl,
  previewContent,
  restrictImage,
  primaryBtnUrlRestricted,
  isInEditor,
  article,
  disableTiers = false,
  children,
  percentageToWatch,
  richTextComponent,
  quizBannerContent,
  quizContentConfig,
}) => {
  const [reduxIsLogged, , isLoaded] = useLoggedUser();
  const isRouteLoggedIn = useIsLoggedIn();
  const isLogged = isLoaded ? reduxIsLogged : isRouteLoggedIn;
  const [isLoading, setIsLoading] = useState(false);
  const loyaltyApi = useLoyaltyApi();
  const [articleData, setArticleData] = useState<ArticleDataPartial>({
    is_available: false,
    is_read: null,
    is_watched: null,
    is_completed: null,
    value: null,
    levels: [],
  });
  const { articleType = "article", articleId } = article;
  const isArticleAvailable = articleData.is_available || disableTiers;
  const isTypeOfArticle = articleType === "article";
  const isShowChildrenContent = isInEditor || (isLogged && isArticleAvailable);
  const { __ } = useTranslations();
  const imageData = getImageSource(article.image ?? null, article.title);
  const { isTypeOfQuiz, isQuizModalOpen, closeQuiz, quizBannerTopProps, quizBannerBottomProps } = useQuizBanner({
    articleType,
    points: articleData.value,
    isCompleted: articleData.is_completed ?? null,
    isLogged,
    isArticleAvailable,
    content: quizBannerContent,
  });

  useEffect(() => {
    if (isInEditor || !articleId) {
      return;
    }

    const getArticleData = async () => {
      try {
        setIsLoading(true);
        const user = getUser();
        const response = (
          await loyaltyApi.getArticles({
            uuid: user.uuid,
            ciam_token: user.ciam_token,
            typedIdentifiers: {
              [articleType]: [article.articleId],
            },
          })
        )[0];

        if (response) {
          setArticleData({
            is_available: response.is_available,
            is_read: response.is_read,
            is_watched: response.is_watched,
            is_completed: response.is_completed,
            value: response.value,
            levels: response.levels,
          });
        }
      } catch (error) {
        console.log(error);
        toast.error(
          translateBackendErrorCode(
            isApiErrorWithErrorCode(error) ? error.error_code : undefined,
            __("loyalty.articleRestrictedContent.errorMessage")
          )
        );
      } finally {
        setIsLoading(false);
      }
    };
    getArticleData();
  }, [__, articleId, isLogged, isInEditor, articleType, loyaltyApi]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.loading}>
            <Loader data-testid="loadingarticle" />
          </div>
        ) : (
          <>
            {quizBannerTopProps && <QuizBannerTop {...quizBannerTopProps} />}

            <ArticleRestrictedContentHeader
              title={article.title}
              subtitle={article.subtitle}
              isRead={articleData.is_read}
              isWatched={articleData.is_watched}
              isCompleted={articleData.is_completed}
              creationDate={article.creationDate}
              points={articleData.value}
              tier={articleData.levels[0]}
              disableTiers={disableTiers}
              image={imageData}
              isAvailable={articleData.is_available}
              video={article.video}
              headerMediaType={article.headerMediaType}
              articleType={article.articleType}
              controlColor={article.controlColor}
              articleId={article.articleId}
              isLogged={isLogged}
              percentageToWatch={percentageToWatch}
            />

            <ArticleRestrictedContentResponsiveContainer>
              {isShowChildrenContent && (
                <>
                  {children}

                  {isTypeOfArticle && article.articleId && (
                    <PointsTrigger
                      articleId={article.articleId}
                      isArticleRead={articleData.is_read}
                      setArticleData={setArticleData}
                      isInEditor={isInEditor}
                      pointsGranted={articleData.value ?? 0}
                    />
                  )}
                </>
              )}

              {isTypeOfQuiz && quizBannerBottomProps && <QuizBannerBottom {...quizBannerBottomProps} />}

              <PreviewContent
                primaryBtnUrl={primaryBtnUrl}
                secondaryBtnUrl={secondaryBtnUrl}
                isLogged={isLogged}
                previewContent={previewContent}
                restrictImage={restrictImage}
                primaryBtnUrlRestricted={primaryBtnUrlRestricted}
                isInEditor={isInEditor}
                isAvailable={isArticleAvailable}
                disableTiers={disableTiers}
                richTextComponent={richTextComponent}
              />
            </ArticleRestrictedContentResponsiveContainer>

            {isTypeOfQuiz && quizContentConfig && (
              <QuizContent
                isOpened={isQuizModalOpen}
                onClose={closeQuiz}
                setArticleData={setArticleData}
                config={quizContentConfig}
                articleId={articleId}
                points={articleData.value}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
